// ============================================================================
// Service-line model + pure helpers for the New Account SOP intake.
//
// A building can run MULTIPLE service lines (night janitorial + a day porter +
// a monthly carpet crew, etc.). ⛔ THE TRUTH IS THE BUILDING'S OWN LINES, and
// has been since 2026-09-12. The onboarding card used to hold its own copy in a
// jsonb column and a seeder fanned that copy out at materialization; the card's
// door now refuses that field outright and the seeder's half that did the
// fanning is gone. This module is the single, DOM-free source of truth for the
// line SHAPE and the hourly→monthly math, shared by the create modal and the
// card editor so the two can never drift.
//
// Every save goes through save_service_line() — one door. It decides for itself
// whether a money field is a line still being SET UP or a rate change on a
// building already being served, and routes the second through the one
// rate-change process so Finance hears about it. ⛔ Removing a line that the
// building carries is STOPPING it on a night somebody names, never a delete:
// a line that has been invoiced has to stay readable.
//
// The keys below are the shape the card's window serves and the patch the door
// takes:
//   assignment_id, assignment_status, start_date, provider_id, service_code,
//   billing_method, client_rate, sub_rate_type, sub_rate_monthly,
//   sub_hourly_rate, sub_hours_per_day, service_days_per_week,
//   contractor_name, notes
// ============================================================================

import { toNumOrNull } from '../../../shared/coerce';
import type { SubMode } from './sub-assignment';

// ⛔ THE VOCABULARY IS NOT DEFINED IN THE FRONTEND AT ALL ANY MORE. It lives in
// the database, the picker reads it live, and the line carries a validated
// reference to it. The two arrays that used to be re-exported from here are
// deleted: a new kind of standing work is a row in the catalogue now, not an
// edit to a TypeScript file that four other modules had to import.

export const NAS_BILLING_METHOD_OPTIONS = [
  'Monthly_Flat',
  'Hourly',
  'Variable',
] as const;

export type NasBillingMethod = (typeof NAS_BILLING_METHOD_OPTIONS)[number];
export type NasSubRateType = 'monthly' | 'hourly';

/**
 * Client-side form shape for one line. All numeric inputs are strings (matching
 * the modal's controlled-input convention); serialize with serviceLineToPatch().
 */
export interface ServiceLineForm {
  // ⛔ WHAT THE SCREEN COULD NOT SEE. These three come from the building's own
  // arrangement, through v_nas_card. Without them the editor had only one
  // question it could ask — "is there a row?" — and it answered "so this is a
  // rate change" to a line that had not started yet, which is how an agreed
  // $450 rendered as a locked 0.00. Blank on a line the building does not
  // carry yet.
  assignment_id: string;
  assignment_status: string;
  start_date: string;
  /** ⛔ The one answer, from the catalogue. Nothing here carries the two
      retired words any more — the rule on the table fills those in. */
  service_code: string;
  /** What the catalogue calls that answer, for anything a person reads. Not
      stored on the line: it comes back with it. */
  service_label: string;
  billing_method: NasBillingMethod;
  client_rate: string;
  // Sub selection (reuses the AFB-0026 sub-assignment logic per line).
  sub_mode: SubMode;
  service_provider_id: string;
  contractor_name: string;
  contractor_legal_name: string;
  contractor_phone: string;
  contractor_email: string;
  // Sub cost — monthly flat OR hourly (rolled up to a monthly figure).
  sub_rate_type: NasSubRateType;
  sub_rate_monthly: string;
  sub_hourly_rate: string;
  sub_hours_per_day: string;
  service_days_per_week: string;
  notes: string;
}

/** A fresh, empty service line with the trigger-matching defaults. */
export function emptyServiceLine(): ServiceLineForm {
  return {
    assignment_id: '',
    assignment_status: '',
    start_date: '',
    // ⛔ No default. A form default is not a person's answer — opening on the
    // main clean is how 62 live records ended up carrying a name the fixed
    // drop-down did not even contain.
    service_code: '',
    service_label: '',
    billing_method: 'Monthly_Flat',
    client_rate: '',
    sub_mode: 'unassigned',
    service_provider_id: '',
    contractor_name: '',
    contractor_legal_name: '',
    contractor_phone: '',
    contractor_email: '',
    sub_rate_type: 'monthly',
    sub_rate_monthly: '',
    sub_hourly_rate: '',
    sub_hours_per_day: '',
    service_days_per_week: '',
    notes: '',
  };
}

/**
 * Hourly → monthly rollup, matching the DB trigger EXACTLY so the intake
 * preview and the materialized service_assignments.sub_rate_monthly agree:
 *   hourly × hours/day × days/week × 52 ÷ 12, rounded to 2 dp.
 * Returns null when any input is missing/zero so the caller can show a dash.
 */
export function computeHourlyMonthly(
  hourly: unknown,
  hoursPerDay: unknown,
  daysPerWeek: unknown,
): number | null {
  const h = toNumOrNull(hourly);
  const hpd = toNumOrNull(hoursPerDay);
  const dpw = toNumOrNull(daysPerWeek);
  if (!h || !hpd || !dpw) return null;
  return Math.round(((h * hpd * dpw * 52) / 12) * 100) / 100;
}

/** The monthly sub cost for a line, whether it's billed monthly or hourly. */
export function lineMonthlySub(line: ServiceLineForm): number | null {
  if (line.sub_rate_type === 'hourly') {
    return computeHourlyMonthly(
      line.sub_hourly_rate,
      line.sub_hours_per_day,
      line.service_days_per_week,
    );
  }
  return toNumOrNull(line.sub_rate_monthly);
}

/** True when an hourly line lacks the inputs needed to roll up a monthly figure. */
export function isEstimatedMonthly(line: ServiceLineForm): boolean {
  return line.sub_rate_type === 'hourly' && lineMonthlySub(line) != null;
}

/**
 * IS THIS LINE LIVE ON THE BUILDING — i.e. is changing its money a RATE CHANGE?
 *
 * ⛔ "A row exists" is NOT the question and asking it was the defect. A line
 * that is Scheduled, or whose first night is still ahead, is being SET UP:
 * naming its price for the first time is not a change to anything, and making
 * an account manager file a rate-change proposal to finish setting up a
 * building nobody has cleaned yet is admin over truth.
 *
 * A line carrying the word Active with no start date at all IS running —
 * Finance reads it — so it keeps rate-change discipline.
 *
 * ⛔ This is the same predicate save_service_line() applies server-side. The
 * screen decides what to SHOW; the database decides what to ALLOW. Keep the two
 * saying the same thing.
 */
export function isLiveOnTheBuilding(
  line: ServiceLineForm,
  today: string = new Date().toISOString().slice(0, 10),
): boolean {
  if (line.assignment_status !== 'Active') return false;
  return line.start_date === '' || line.start_date <= today;
}

/** True once the building carries this line at all, whatever its status. */
export function isOnTheBuilding(line: ServiceLineForm): boolean {
  return line.assignment_id.trim() !== '';
}

/**
 * The patch save_service_line() takes for one line. Field names are the
 * service_assignments columns, because that is the record being written — the
 * jsonb element shape was the CARD's copy of it and is on its way out.
 */
export function serviceLineToPatch(line: ServiceLineForm): Record<string, unknown> {
  const patch: Record<string, unknown> = {
    // ⛔ The answer only. The two retired columns are filled from it by the rule
    // on the table, so sending them here would be a second place to disagree.
    service_code: line.service_code || null,
    billing_method: line.billing_method || 'Monthly_Flat',
    sub_rate_type: line.sub_rate_type || 'monthly',
    // ⛔ Always sent, including null: a cleared figure has to clear the column
    // rather than leave the old one standing.
    billing_rate: toNumOrNull(line.client_rate),
    provider_id: line.service_provider_id.trim() || null,
    worker_name: line.contractor_name.trim() || null,
    notes: line.notes.trim() || null,
  };
  if (line.sub_rate_type === 'hourly') {
    patch.sub_hourly_rate = toNumOrNull(line.sub_hourly_rate);
    patch.sub_hours_per_day = toNumOrNull(line.sub_hours_per_day);
    patch.service_days_per_week = toNumOrNull(line.service_days_per_week);
  } else {
    patch.sub_rate_monthly = toNumOrNull(line.sub_rate_monthly);
    patch.service_days_per_week = toNumOrNull(line.service_days_per_week);
  }
  // ⛔ A LINE THE BUILDING DOES NOT CARRY YET IS ADDED, never matched (item 847).
  // With no id, the door used to look for an existing line of the same kind and
  // save over it; a building can carry two lines of one kind (Houston has two
  // Main clean lines), so a new one says it is new.
  if (!isOnTheBuilding(line)) patch.new_line = true;
  return patch;
}

// ⛔ THE CARD NO LONGER SERIALIZES ITS LINES. serviceLineToJson() and
// serviceLinesToJson() lived here to fill new_account_sop.service_lines, the
// jsonb the seeder fanned out onto a building. The seeder was retired on
// 2026-09-17 and the column was dropped on 2026-09-18. A line is written with
// serviceLineToPatch() through save_service_line(), and nowhere else — leaving
// a serializer for a column that does not exist is how a second writer comes
// back.

/** A line is meaningful once it has a client rate, any sub cost, or a sub pick. */
export function isMeaningfulLine(line: ServiceLineForm): boolean {
  return (
    toNumOrNull(line.client_rate) != null ||
    lineMonthlySub(line) != null ||
    line.sub_mode !== 'unassigned' ||
    line.service_provider_id.trim() !== '' ||
    line.contractor_name.trim() !== ''
  );
}

/**
 * The card's money totals — the SUM of its lines, on both sides.
 *
 * ⛔ A card total is never typed independently of its lines (2026-09-08). It
 * used to be: `final_pricing` was a header field and line 1 borrowed it, so a
 * second line's price was captured on the line and NOT in the total. A header
 * figure that disagrees with the sum of the lines is the error shape that
 * produced two onboarding cards for one Houston hospital — one card holding
 * the nightly crew, one holding the day porter, each total "right" on its own.
 *
 * Returns null on a side with nothing to add up, so a card with no money
 * answers yet reads as blank rather than as $0 (a typed zero is a claim and a
 * blank is not — TCSD-0175).
 */
export function serviceLineTotals(lines: ServiceLineForm[]): {
  client: number | null;
  sub: number | null;
} {
  const meaningful = lines.filter(isMeaningfulLine);
  const sum = (vals: (number | null)[]): number | null => {
    const present = vals.filter((v): v is number => v != null);
    if (present.length === 0) return null;
    return Math.round(present.reduce((a, b) => a + b, 0) * 100) / 100;
  };
  return {
    // ⛔ A line billed by the hour has no monthly client figure (items
    // 831/758): its rate is per hour, and adding it here is how Belton's card
    // came to hold $26 as a "Client Monthly Price". Left out of the sum.
    client: sum(
      meaningful.filter((l) => l.billing_method !== 'Hourly').map((l) => toNumOrNull(l.client_rate)),
    ),
    sub: sum(meaningful.map(lineMonthlySub)),
  };
}

// ⛔ THE MIRROR IS GONE. primaryLineMirror() returned the seven columns the
// card kept alongside its lines — sub_payment, service_provider_id,
// is_new_subcontractor and the three contractor_* fields. All seven were
// dropped from new_account_sop on 2026-09-18; the card's window serves them
// from the building now. The one figure that survived, final_pricing, is just
// serviceLineTotals(lines).client, so the caller computes it directly.

// ---------------------------------------------------------------------------
// Draft hydration — reconstruct the repeater from a persisted row.
// ---------------------------------------------------------------------------

/** Subset of new_account_sop we hydrate from (kept loose to avoid a cycle). */
export interface ServiceLineSourceRow {
  service_lines?: unknown;
  final_pricing?: number | null;
  sub_payment?: number | null;
  service_provider_id?: string | null;
  is_new_subcontractor?: boolean | null;
  contractor_name?: string | null;
  contractor_phone?: string | null;
  contractor_email?: string | null;
  service_days_per_week?: number | null;
}

const s = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v);

/** Coerce one persisted jsonb element back into a form line. */
function jsonToServiceLine(raw: Record<string, unknown>): ServiceLineForm {
  const base = emptyServiceLine();
  const subRateType: NasSubRateType =
    raw.sub_rate_type === 'hourly' ? 'hourly' : 'monthly';
  const providerId = s(raw.provider_id);
  const contractorName = s(raw.contractor_name);
  const subMode: SubMode = providerId
    ? 'previous'
    : contractorName
      ? 'new'
      : 'unassigned';
  return {
    ...base,
    assignment_id: s(raw.assignment_id),
    assignment_status: s(raw.assignment_status),
    start_date: s(raw.start_date),
    service_code: s(raw.service_code) || base.service_code,
    service_label: s(raw.service_label) || base.service_label,
    billing_method: (s(raw.billing_method) || base.billing_method) as NasBillingMethod,
    client_rate: s(raw.client_rate),
    sub_mode: subMode,
    service_provider_id: providerId,
    contractor_name: contractorName,
    sub_rate_type: subRateType,
    sub_rate_monthly: subRateType === 'monthly' ? s(raw.sub_rate_monthly) : '',
    sub_hourly_rate: s(raw.sub_hourly_rate),
    sub_hours_per_day: s(raw.sub_hours_per_day),
    service_days_per_week: s(raw.service_days_per_week),
    notes: s(raw.notes),
  };
}

/**
 * Reconstruct the repeater from a row. Prefers the service_lines jsonb; falls
 * back to a single line built from the legacy final_pricing/sub_payment/sub
 * columns so pre-service-lines drafts open cleanly. Always returns ≥1 line.
 */
export function serviceLinesFromRow(row: ServiceLineSourceRow): ServiceLineForm[] {
  const arr = row.service_lines;
  if (Array.isArray(arr) && arr.length > 0) {
    return arr.map((el) =>
      jsonToServiceLine((el ?? {}) as Record<string, unknown>),
    );
  }
  // Legacy single-line fallback.
  const legacy = emptyServiceLine();
  legacy.client_rate = s(row.final_pricing);
  legacy.sub_rate_monthly = s(row.sub_payment);
  legacy.service_days_per_week = s(row.service_days_per_week);
  if (row.service_provider_id) {
    legacy.sub_mode = 'previous';
    legacy.service_provider_id = row.service_provider_id;
  } else if (row.is_new_subcontractor) {
    legacy.sub_mode = 'new';
    legacy.contractor_name = s(row.contractor_name);
    legacy.contractor_phone = s(row.contractor_phone);
    legacy.contractor_email = s(row.contractor_email);
  }
  return [legacy];
}
